import { useState } from "react";
import { useLocation, Navigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { api } from "@/lib/api";
import { redirectToStripe } from "@/lib/redirect";

/**
 * Focused "pay what you can" deep-link.
 *
 * Sits outside the SiteGate so anyone with the URL can purchase in two
 * taps — pick an amount, hit checkout, land on Stripe.
 *
 *   /buy/wav-pwyc   →  pre-order the Metamorph audio package
 *   /buy/film-pwyc  →  Myth · Live Performance Film
 *
 * Uses the same /api/checkout/cart endpoint as the storefront so PDF
 * tickets, confirmation emails and the daily digest fire identically.
 */
const VARIANTS = {
  "wav-pwyc": {
    type: "wav",
    title: "Metamorph · 9 Min Audio Package",
    subtitle: "Lyrics · WAV quality audio",
    presets: [10, 15, 20, 30],
    minAmount: 1,
  },
  "film-pwyc": {
    type: "myth_film",
    title: "Myth · Live Performance Film",
    subtitle: "Limited download",
    presets: [20, 30, 50, 100],
    minAmount: 20,
  },
};

export default function QuickPayWhatYouCan() {
  const { pathname } = useLocation();
  // Pull the slug from the URL — works for both static (`/buy/wav-pwyc`)
  // and the future-proof dynamic pattern (`/buy/:slug`).
  const slug = pathname.split("/").pop();
  const variant = VARIANTS[slug];
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!variant) return <Navigate to="/" replace />;

  const onCheckout = async () => {
    const price = parseFloat(amount);
    if (!price || price < (variant.minAmount || 1)) return;
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await api.post("/checkout/cart", {
        items: [{ type: variant.type, custom_price: price, quantity: 1 }],
        origin_url: window.location.origin,
      });
      if (data?.url) {
        redirectToStripe(data.url);
      } else {
        setError("Checkout could not be started. Please try again.");
        setSubmitting(false);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <main
      className="min-h-screen flex flex-col items-center justify-center bg-[var(--mm-bg,#050a1a)] text-white px-6 py-12"
      data-testid="quick-pwyc"
    >
      <div className="w-full max-w-sm">
        <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-white/55 text-center mb-3">
          by George Maple
        </p>
        <h1
          className="deco-text text-4xl sm:text-5xl uppercase text-center leading-[0.95] mb-3"
          style={{ letterSpacing: "0.1em" }}
        >
          Metamorph
        </h1>
        <h2
          className="text-xl sm:text-2xl text-center mb-2"
          style={{ fontFamily: "'Italiana', serif", color: "#f3f2ee", letterSpacing: "0.04em" }}
        >
          {variant.title}
        </h2>
        <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-white/55 text-center mb-10">
          {variant.subtitle}
        </p>

        <p className="font-mono text-xs uppercase tracking-[0.28em] text-white/70 text-center mb-2 font-medium">
          Pay what you can
        </p>
        <div className="flex items-baseline justify-center gap-1.5 mb-5 border-b border-white/25 focus-within:border-[var(--mm-accent)] py-2 max-w-xs mx-auto">
          <span
            className="text-base leading-none text-white/55"
            style={{ fontFamily: "'Italiana', serif" }}
          >
            $
          </span>
          <input
            type="number"
            inputMode="decimal"
            min={variant.minAmount || 1}
            step="0.5"
            autoFocus
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="enter amount"
            className="pwyc-input bg-transparent outline-none text-lg text-center text-white w-32 leading-none placeholder:text-white/35 placeholder:italic"
            style={{ fontFamily: "'Italiana', serif" }}
            data-testid="pwyc-amount"
          />
          <span
            className="font-mono text-[10px] text-white/45 tracking-[0.32em] font-medium leading-none"
          >
            AUD
          </span>
        </div>

        <p className="font-mono text-xs uppercase tracking-[0.28em] text-white/50 text-center mb-3 font-medium">
          Or choose
        </p>
        <div className="grid grid-cols-4 gap-1.5 mb-6">
          {variant.presets.map((p) => {
            const active = parseFloat(amount) === p;
            return (
              <button
                key={p}
                type="button"
                onClick={() => setAmount(String(p))}
                className={`py-3 border text-base font-mono font-medium tracking-wide transition-all duration-300 ${
                  active
                    ? "border-[var(--mm-accent)] text-[var(--mm-accent-hover)] bg-[rgba(184,148,63,0.10)]"
                    : "border-white/25 text-white/85 hover:border-white/60 hover:text-white"
                }`}
                data-testid={`pwyc-preset-${p}`}
              >
                ${p}
              </button>
            );
          })}
        </div>

        <button
          onClick={onCheckout}
          disabled={!amount || parseFloat(amount) < (variant.minAmount || 1) || submitting}
          className="btn-line w-full justify-center disabled:opacity-30 disabled:cursor-not-allowed text-sm py-3"
          data-testid="pwyc-checkout"
        >
          {submitting ? "Securing your spot…" : (<>Continue to checkout <Plus className="w-4 h-4" strokeWidth={1.4} /></>)}
        </button>

        {amount && parseFloat(amount) < (variant.minAmount || 1) && (
          <p className="font-mono text-[10px] text-rose-300/75 text-center mt-3 tracking-wide" data-testid="pwyc-min-warning">
            Minimum ${variant.minAmount} AUD
          </p>
        )}

        {error && (
          <p className="font-mono text-[11px] text-rose-300/80 text-center mt-4" data-testid="pwyc-error">
            {error}
          </p>
        )}
      </div>
    </main>
  );
}
